import { useState, useMemo } from "react";
import { cn } from "@/nucleo/utilitarios";
import { ChevronDown, ChevronRight, Search, Plus, type LucideIcon } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Input } from "@/ui/base/input";
import { Button } from "@/ui/base/button";
import { ScrollArea } from "@/ui/base/scroll-area";
import type { DocArticle } from "@/lib/useDocs";

interface DocsSidebarProps {
  articles: DocArticle[];
  activeSlug: string | null;
  onSelect: (slug: string) => void;
  isAdmin?: boolean;
  onNewArticle?: () => void;
}

export function DocsSidebar({ articles, activeSlug, onSelect, isAdmin, onNewArticle }: DocsSidebarProps) {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Build tree: root articles (no parent) with children
  const rootArticles = useMemo(() => {
    return articles
      .filter((a) => !a.parent_id)
      .sort((a, b) => a.ordem - b.ordem);
  }, [articles]);

  const childrenOf = (parentId: string) =>
    articles.filter((a) => a.parent_id === parentId).sort((a, b) => a.ordem - b.ordem);

  const filtered = useMemo(() => {
    if (!search.trim()) return rootArticles;
    const q = search.toLowerCase();
    return rootArticles.filter(
      (a) =>
        a.titulo.toLowerCase().includes(q) ||
        childrenOf(a.id).some((c) => c.titulo.toLowerCase().includes(q))
    );
  }, [rootArticles, search, articles]);

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as unknown as Record<string, LucideIcon>)[iconName] || LucideIcons.FileText;
    return <Icon className="h-4 w-4 shrink-0" />;
  };

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex flex-col h-full border-r border-border bg-muted/30">
      <div className="p-3 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar artigo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        {isAdmin && onNewArticle && (
          <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={onNewArticle}>
            <Plus className="h-3.5 w-3.5" /> Novo artigo
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1">
        <nav className="p-2 space-y-0.5" aria-label="Documentação">
          {filtered.map((article) => {
            const children = childrenOf(article.id);
            const hasChildren = children.length > 0;
            const isCollapsed = collapsed[article.id] ?? false;
            const isActive = activeSlug === article.slug;

            return (
              <div key={article.id}>
                <button
                  onClick={() => {
                    onSelect(article.slug);
                    if (hasChildren) toggleCollapse(article.id);
                  }}
                  className={cn(
                    "flex items-center gap-2 w-full px-2.5 py-2 text-sm rounded-lg transition-colors text-left",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/70 hover:bg-muted hover:text-foreground"
                  )}
                >
                  {getIcon(article.icone)}
                  <span className="flex-1 truncate">{article.titulo}</span>
                  {hasChildren && (
                    isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
                {hasChildren && !isCollapsed && (
                  <div className="ml-4 pl-2 border-l border-border/50 space-y-0.5 mt-0.5">
                    {children.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => onSelect(child.slug)}
                        className={cn(
                          "flex items-center gap-2 w-full px-2.5 py-1.5 text-sm rounded-lg transition-colors text-left",
                          activeSlug === child.slug
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-foreground/60 hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {getIcon(child.icone)}
                        <span className="truncate">{child.titulo}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>
    </div>
  );
}
